<template>
  <div class="block block-rounded">
    <div v-if="loading">
      <loadingspinner></loadingspinner>
    </div>
    <div class="block-header block-header-default">
      <h3 class="block-title">
        {{
          formFields.id !== null
            ? "Update Invoice " + formFields.id
            : "Create Invoice"
        }}
        <span v-if="props.customer">for {{ customer.name }}</span>
      </h3>
    </div>
    <div>
      <div
        v-if="formFields.vat_reverse_charge"
        class="alert alert-warning mx-2 my-2"
      >
        <i class="fa fa-exclamation-triangle"></i>
        &nbsp;
        <strong>Reverse Charge VAT</strong>
        - This invoice is subject to reverse charge VAT. VAT will be removed in
        Xero.
      </div>
      <form id="invoice" method="POST" @submit.prevent="saveInvoice">
        <fieldset :disabled="props.locked">
          <div class="block-content block-content-full">
            <div class="row push">
              <div class="col-lg-4">
                <div class="fs-sm text text-muted">
                  <h5>Customer Details</h5>
                  <p v-if="formFields.customer && formFields.customer.name">
                    <strong>{{ formFields.customer.name }}</strong>
                    <br />
                    <span
                      v-if="formFields.customer.office_address.full_address"
                    >
                      {{ formFields.customer.office_address.full_address }}
                    </span>
                    <br />
                    <label for="reference" class="mt-2 fw-bold">
                      Reference
                    </label>
                    <input
                      v-model="formFields.reference"
                      type="text"
                      name="reference"
                      class="form-control mt-2"
                    />
                    <label for="vat_reverse_charge" class="mt-2 fw-bold">
                      Reverse VAT
                    </label>
                    &nbsp;
                    <input
                      v-model="formFields.vat_reverse_charge"
                      type="checkbox"
                      name="vat_reverse_charge"
                      class="mt-2"
                    />
                  </p>
                </div>
              </div>
              <div v-if="formFields.customer" class="col-lg-4">
                <div class="fs-sm text text-muted">
                  <h5 class="mb-1">Site Address</h5>
                  <span v-if="formFields.address.line_1">
                    {{ formFields.address.line_1 }},
                  </span>
                  <span v-if="formFields.address.line_2">
                    {{ formFields.address.line_2 }},
                  </span>
                  <span v-if="formFields.address.city">
                    {{ formFields.address.city }},
                  </span>
                  <span v-if="formFields.address.postcode">
                    {{ formFields.address.postcode }}
                  </span>
                </div>
                <div class="fs-sm text text-muted">
                  <h5 class="mb-1">Contact</h5>
                  <span v-if="formFields.contact.name">
                    <strong>Name :</strong>
                    {{ formFields.contact.name }}
                    <br />
                  </span>
                  <span v-if="formFields.contact.email">
                    <strong>Email :</strong>
                    {{ formFields.contact.email }}
                    <br />
                  </span>
                  <span v-if="formFields.contact.phone">
                    <strong>Phone :</strong>
                    {{ formFields.contact.phone }}
                  </span>
                </div>
              </div>
              <div v-if="formFields.id" class="col-lg-4">
                <div class="fs-sm text text-muted">
                  <h5>Invoice Details</h5>
                  <span v-if="formFields.status">
                    <strong>Status:</strong>
                    <span
                      :class="[
                        statusClasses[formFields.status],
                        'mx-2 rounded-pill',
                      ]"
                      style="font-size: 0.85rem"
                    >
                      <strong>{{ formFields.status }}</strong>
                    </span>
                    <br />
                  </span>
                  <span v-if="formFields.invoice_number">
                    <strong>Invoice Number:</strong>
                    {{ formFields.invoice_number }}
                    <br />
                  </span>
                  <span v-if="formFields.quote_id">
                    <strong>Quote Reference:&nbsp;</strong>
                    <a :href="quoteLink">{{ formFields.quote.reference }}</a>
                    <br />
                  </span>
                  <span v-if="formFields.created_at">
                    <strong>Created:</strong>
                    {{ $formatDateTime(formFields.created_at) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="block-content block-content-full">
            <div class="row push">
              <div class="col-lg-12 col-xl-12">
                <h5 class="text-muted">Line Items</h5>
                <div class="mb-4">
                  <table class="table table-striped w-full">
                    <thead>
                      <tr>
                        <th class="col-4">
                          Description
                          <span class="text-danger">*</span>
                        </th>
                        <th>
                          Quantity
                          <span class="text-danger">*</span>
                        </th>
                        <th>
                          Price
                          <span class="text-danger">*</span>
                        </th>
                        <th>
                          VAT Rate
                          <span class="text-danger">*</span>
                        </th>
                        <th style="width: 15%"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="formFields.items">
                        <tr
                          v-for="(item, index) in formFields.items"
                          :key="index"
                        >
                          <td>
                            <input
                              v-model="item.description"
                              type="text"
                              class="form-control"
                              :name="`items[${index}][description]`"
                              required
                            />
                          </td>
                          <td>
                            <input
                              v-model="item.quantity"
                              type="number"
                              class="form-control"
                              :name="`items[${index}][quantity]`"
                              step="1"
                              required
                            />
                          </td>
                          <td>
                            <div class="input-group">
                              <span class="input-group-text">£</span>
                              <input
                                v-model="item.unit_price['major_value']"
                                type="number"
                                step=".01"
                                class="form-control"
                                :name="`items[${index}][unit_price]`"
                                required
                              />
                            </div>
                          </td>
                          <td>
                            <div class="input-group">
                              <Select
                                v-model="item.tax_rate"
                                :name="`items[${index}][tax_rate]`"
                                :values="taxRates"
                                :field-name="`fullLabel`"
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              class="btn btn-sm btn-alt-danger"
                              @click="confirmRemoveItem(index)"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-content-end">
                    <table>
                      <tbody>
                        <tr>
                          <td class="fw-bold text-end">Total Net:</td>
                          <td>
                            {{ formatCurrency(totalNetCost) }}
                          </td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-end">VAT:</td>
                          <td>
                            {{ formatCurrency(totalVatCost) }}
                          </td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-end">Grand Total:</td>
                          <td>
                            {{ formatCurrency(totalGrossCost) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
    <div
      class="block-content block-content-full block-content-sm bg-body-light text-end"
    >
      <button
        v-if="!props.locked"
        type="submit"
        form="invoice"
        class="btn btn-alt-success"
        :class="{ disabled: !canBeSaved() }"
        :disabled="!canBeSaved()"
      >
        {{ formFields.id !== null ? "Update Invoice" : "Create Invoice" }}
      </button>
      <template v-if="formFields.xero_id">
        <a
          :href="formFields.xero_url"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-info ms-2"
        >
          <i class="fa fa-fw fa-external-link-alt"></i>
          View in Xero
        </a>
      </template>
      <template v-if="formFields.online_invoice_url">
        <a
          :href="formFields.online_invoice_url"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-secondary ms-2"
        >
          <i class="fa fa-fw fa-user"></i>
          View as Customer
        </a>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { delay } from "lodash";
import { useFetch } from "@/components/useFetch.js";
import Select from "@/components/form/select.vue";
import loadingspinner from "@/components/form/loadingspinner.vue";

const loading = ref(true);
const taxRates = ref([]);
const hasUnsavedChanges = ref(false);
const isSaving = ref(false);
const quoteLink = ref(null);

const props = defineProps({
  route: String,
  statusClasses: {
    type: Object,
  },
  xeroConnected: {
    type: Boolean,
    default: false,
  },
  edit: {
    type: Boolean,
    default: false,
  },
  locked: {
    type: Boolean,
    default: false,
  },
});

const formFields = ref({
  id: null,
  reference: null,
});
async function getInvoiceDetails() {
  async function getTaxRates() {
    const response = await useFetch(route("api.tax-rates.rates-array"));
    taxRates.value = await response.json();
  }

  await getTaxRates();

  loading.value = true;
  const Request = await useFetch(props.route);
  formFields.value = await Request.json();
  loading.value = false;

  async function matchVatRate() {
    formFields.value.items.forEach((item) => {
      item.tax_rate = taxRates.value.find(
        (rate) => rate.id === item.tax_rate_id
      );
    });
  }

  await matchVatRate();

  quoteLink.value = route("quotes.edit", {
    quote: formFields.value.quote_id,
  });

  // Reset hasUnsavedChanges after loading data
  hasUnsavedChanges.value = false;
}

const totalVatCost = ref(0);
const totalGrossCost = ref(0);
const totalNetCost = computed(() => {
  return totalGrossCost.value - totalVatCost.value;
});

function formatCurrency(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(value);
}

async function saveInvoice() {
  try {
    isSaving.value = true; // Set isSaving to true when save action is triggered
    if (invoice?.id === null || invoice?.id === undefined) {
      const response = await useFetch(
        route("api.invoices.store", [formFields.value.customer_id]),
        {
          method: "POST",
          body: formFields.value,
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Redirect to the invoice page
        window.location.href = route("invoices.edit", data.id);
      } else {
        const errorData = await response.json();
        Swal.fire({
          title: "Error",
          text: errorData.message,
          icon: "error",
        });
      }
    } else {
      const response = await useFetch(
        route("api.invoices.update", [formFields.value.id]),
        {
          method: "PATCH",
          body: formFields.value,
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Redirect to the quote page
        window.location.href = route("invoices.edit", data.id);
      } else {
        const errorData = await response.json();
        Swal.fire({
          title: "Error",
          text: errorData.message,
          icon: "error",
        });
      }
    }
  } catch (error) {
    Swal.fire({
      title: "Error",
      text: error,
      icon: "error",
    });
  } finally {
    isSaving.value = false; // Reset isSaving to false after save action is complete
  }
}

function confirmRemoveItem(index, type) {
  Swal.fire({
    title: "Are you sure?",
    text: "This line item will be removed from the Invoice",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete it!",
    confirmButtonColor: "#d33",
    cancelButtonText: "No, cancel!",
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      removeItem(index, type);
    }
  });
}

function removeItem(index) {
  formFields.value.items.splice(index, 1);
}

function calculateTotals() {
  // Calculate the total for each item
  formFields.value.items.forEach((item) => {
    // make sure the initial hire price is set
    if (!item.unit_price) {
      return;
    }

    // Ensure unit_price is a float with 2 decimal places
    item.unit_price["major_value"] = parseFloat(
      item.unit_price["major_value"]
    ).toFixed(2);

    // Calculate the total for the item
    item.vat_amount =
      (item.unit_price["major_value"] * item.quantity * item.tax_rate.rate) /
      100;
    item.total_with_vat =
      item.unit_price["major_value"] * item.quantity + item.vat_amount;
  });

  if (formFields.value.items.length === 0) {
    return;
  }

  // Calculate the total VAT cost
  totalVatCost.value = formFields.value.items.reduce((acc, item) => {
    if (!item.vat_amount) {
      return acc;
    }
    return acc + item.vat_amount;
  }, 0);

  // Calculate the total cost
  let total = 0;
  formFields.value.items.forEach((item) => {
    total += item.total_with_vat;
  });
  totalGrossCost.value = total;
}

if (formFields.value?.items) {
  // loop through each item and set the price to the major value of the array
  formFields.value.items.forEach((item) => {
    item.price = parseInt(item.unit_price["major_value"], 10);
    item.overhire_rate = parseInt(item.overhire_rate["major_value"], 10);
  });
  calculateTotals();
}

watch(
  formFields,
  () => {
    calculateTotals();
    hasUnsavedChanges.value = true;
  },
  { deep: true }
);

// Add beforeunload event listener
onMounted(() => {
  if (props.edit) {
    getInvoiceDetails();
    // Reset hasUnsavedChanges after loading data to ensure it is not triggered on page load
    delay(() => {
      hasUnsavedChanges.value = false;
    }, 1000);
  }
  window.addEventListener("beforeunload", handleBeforeUnload);
});

function handleBeforeUnload(event) {
  if (hasUnsavedChanges.value && !isSaving.value) {
    event.preventDefault();
    event.returnValue = "";
  }
}

function canBeSaved() {
  return !!(
    formFields.value.customer_id &&
    formFields.value.address_id &&
    formFields.value.contact_id
  );
}
</script>
